import { useState, useEffect, useRef } from 'react';
import { flightDenoms, getChipScale } from '../../utilities/chips';
import { chipStyle } from './ChipStack';
import { playChipsMed2, playChipsBig, playWinningBell } from '../../utilities/sounds';

// Must match the .chip-flight transition duration in ChipStack.css
const FLIGHT_MS = 550;
const FLIGHT_SETTLE_MS = FLIGHT_MS + 130;

const STAGE_INDEX = { waiting: 0, start: 0, preflop: 1, flop: 2, turn: 3, river: 4, showdown: 5 };
const stageIndex = (r) => STAGE_INDEX[r] ?? 0;

// Current bets per seat from a game snapshot (folded players keep their bet visible
// until the stage's chips are collected, like a real table).
const liveBets = (g) => {
  const bets = {};
  Object.entries(g?.players || {}).forEach(([seat, p]) => {
    if (p && !p.isEmpty && p.currentBet > 0) bets[seat] = p.currentBet;
  });
  return bets;
};

// Sum of every seat's current-stage bet.
const sumLiveBets = (g) => Object.values(liveBets(g)).reduce((a, b) => a + b, 0);

// Number of players seated at the table (non-empty seats).
const seatedCount = (g) =>
  Object.values(g?.players || {}).filter((p) => p && !p.isEmpty).length;

// The "settled" pot — what's actually been swept into the middle from completed
// stages. The backend folds each bet into game.pot the instant it's made, so the
// raw pot grows on every action; subtracting the live stage bets keeps the displayed
// pot stable during a stage and only stepping up between stages when bets collect.
const settledPot = (g) => Math.max(0, (g?.pot || 0) - sumLiveBets(g));

// Round-ending events (showdown + everyone-folds) carry winners: [{seat, amount}].
const parseWinners = (winners) =>
  Array.isArray(winners)
    ? winners.filter((w) => w && w.seat != null).map((w) => ({ seat: w.seat, amount: w.amount || 0 }))
    : [];

// Per-table chip animation state machine. Diffs consecutive game snapshots (stamped
// with lastEventType/eventSeq by socketMiddleware) and owns what the table SHOWS:
//  - displayedBets: {seat: amount} — bet stacks in front of players
//  - displayedPot: pot value/pile, deliberately lagging the live value during flights
//  - flights: chip clones currently flying (bets → pot, or pot → winner)
export const useChipAnimations = (gameId, game, geometry) => {
  const [displayedBets, setDisplayedBets] = useState({});
  const [displayedPot, setDisplayedPot] = useState(0);
  const [flights, setFlights] = useState([]);

  const prevGameRef = useRef(null);
  const timersRef = useRef(new Set());
  const phaseRef = useRef('idle');
  const flightIdRef = useRef(0);
  // Latest table geometry, read at event time so flights use current ring positions
  // without re-running the animation effect on every table resize.
  const geometryRef = useRef(geometry);
  geometryRef.current = geometry;

  // Clear any pending timers when the table unmounts (leave / split-screen close)
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (!game) {
      prevGameRef.current = null;
      return;
    }
    const prev = prevGameRef.current;
    prevGameRef.current = game;

    const schedule = (fn, ms) => {
      const id = setTimeout(() => {
        timersRef.current.delete(id);
        fn();
      }, ms);
      timersRef.current.add(id);
    };

    const snap = () => {
      setDisplayedBets(liveBets(game));
      setDisplayedPot(settledPot(game));
      setFlights([]);
      phaseRef.current = 'idle';
    };

    // First snapshot (open table / reconnect) — mirror the state, no animations
    if (!prev) {
      snap();
      return;
    }

    const newEvent = (game.eventSeq || 0) !== (prev.eventSeq || 0);
    if (!newEvent) {
      // Non-game update (player joined/left, REST refresh): quietly resync when idle
      if (phaseRef.current === 'idle') snap();
      return;
    }

    // A new server event arrived mid-animation — fast-forward: drop flights/timers
    // and classify the new event from live state (redux is the source of truth).
    if (phaseRef.current !== 'idle') {
      timersRef.current.forEach(clearTimeout);
      timersRef.current.clear();
      setFlights([]);
      phaseRef.current = 'idle';
    }

    // Background tab: timers/transitions are throttled — just snap
    if (typeof document !== 'undefined' && document.hidden) {
      snap();
      return;
    }

    const scale = getChipScale(game.smallBlind, game.bigBlind);
    const prevBets = liveBets(prev);
    const hadBets = Object.keys(prevBets).length > 0;

    // Bets fly from each seat into the pot; the pot steps up to `endPot` on arrival.
    // `holdPot` is shown (held) during the flight so the value lags until chips land.
    const startCollect = (holdPot, endPot, after) => {
      phaseRef.current = 'collecting';
      setFlights(
        Object.entries(prevBets).map(([seat, bet]) => ({
          id: ++flightIdRef.current,
          from: geometryRef.current.betPositions[seat] || geometryRef.current.seatPositions[seat] || geometryRef.current.potPosition,
          to: geometryRef.current.potPosition,
          denoms: flightDenoms(bet, scale),
        }))
      );
      setDisplayedBets({}); // collected bets leave the felt immediately
      setDisplayedPot(holdPot);
      playChipsMed2();
      schedule(() => {
        setFlights([]);
        setDisplayedPot(endPot);
        if (after) after();
        else phaseRef.current = 'idle';
      }, FLIGHT_SETTLE_MS);
    };

    // The full pot flies to the winning seat(s), then disappears.
    const startAward = (winners, awardPot) => {
      if (winners.length === 0 || awardPot <= 0) {
        setDisplayedBets({});
        setDisplayedPot(0);
        setFlights([]);
        phaseRef.current = 'idle';
        return;
      }
      phaseRef.current = 'awarding';
      setDisplayedBets({}); // felt is empty between round-end and the next deal
      setDisplayedPot(awardPot);
      setFlights(
        winners.map((w) => ({
          id: ++flightIdRef.current,
          from: geometryRef.current.potPosition,
          to: geometryRef.current.seatPositions[w.seat] || geometryRef.current.potPosition,
          denoms: flightDenoms(w.amount, scale),
        }))
      );
      playChipsBig();
      // Skip the celebratory bell when the win comes from everyone else leaving
      // mid-hand — the lone remaining player still wins the pot, but it's not a
      // real showdown win, so no fanfare.
      if (seatedCount(game) > 1) playWinningBell();
      schedule(() => {
        setFlights([]);
        setDisplayedPot(0); // pot has been awarded; the next deal snaps to blinds
        phaseRef.current = 'idle';
      }, FLIGHT_SETTLE_MS);
    };

    if (game.lastEventType === 'GAME_SHOWDOWN') {
      // Round ended (showdown or everyone-folds). The backend already includes every
      // bet in prev.pot, so that's the full pot; collect any remaining stage bets
      // into it, then fly it to the winner(s) named in the payload.
      const winners = parseWinners(game.winners);
      const fullPot = prev.pot || 0;
      if (hadBets) startCollect(settledPot(prev), fullPot, () => startAward(winners, fullPot));
      else startAward(winners, fullPot);
    } else if (game.lastEventType === 'GAME_DEAL') {
      // New round — blinds appear as stacks over an empty (settled) pot, no animation
      snap();
    } else if (hadBets && stageIndex(game.currentStage) > stageIndex(prev.currentStage) && stageIndex(prev.currentStage) >= 1) {
      // A betting stage advanced (preflop→flop→turn→river): sweep the stage's bets
      // into the pot. settledPot is identical before and after except the bets move
      // from the felt into the middle.
      startCollect(settledPot(prev), settledPot(game), null);
    } else {
      // Ordinary bet/call/raise/fold within a stage — pot stays put (settled value)
      snap();
    }
  }, [gameId, game]);

  return { displayedBets, displayedPot, flights };
};

// One flying clone: a tiny chip stack absolutely positioned in table percent
// coordinates, transitioning bottom/left (see .chip-flight in ChipStack.css).
// Completion is finalized by the hook's timer, not onTransitionEnd, so hidden
// tabs or unmounts can never strand the animation state.
export const ChipFlight = ({ from, to, denoms }) => {
  const [pos, setPos] = useState(from);

  useEffect(() => {
    let second;
    // Double rAF guarantees the start position paints before the transition begins
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setPos(to));
    });
    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
  }, [to]);

  return (
    <div className="chip-flight" style={{ bottom: pos.bottom, left: pos.left }}>
      {denoms.map((d, i) => (
        <div key={i} className="chip" style={chipStyle(d)} />
      ))}
    </div>
  );
};
